import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const work = [
  {
    slug: "bnsi-redesign",
    n: "01",
    title: "BNSI Data Portal",
    tag: "Product & Web UX",
    teaser:
      "Redesigning a national statistics portal so anyone — from policymakers to students — can find, read, and trust official data.",
    thumb: "/case-studies/bnsi/home.webp",
    accent: "from-[#4C1D95] to-[#4C1D95]",
  },
  {
    slug: "dialogue",
    n: "02",
    title: "Dialogue",
    tag: "Product Design · AI",
    teaser:
      "An AI audiobook app — designing the AI feature flows and a value-first onboarding that helped users get to their first result faster.",
    thumb: "/case-studies/dialogue/overview-card.webp",
    accent: "from-[#4C1D95] to-[#7C3AED]",
  },
  {
    slug: "rehab-squared-pro",
    n: "03",
    title: "Rehab Squared",
    tag: "Platform UX Consulting",
    teaser:
      "Bringing operational clarity to a multi-sided renovation platform — defining three user types and optimizing the business logic that runs them.",
    thumb: "/case-studies/rehab/rehab-card.webp",
    accent: "from-[#4C1D95] to-[#A78BFA]",
  },
  {
    slug: "fishcaddy-ux-audit",
    n: "04",
    title: "FishCaddy",
    tag: "UX Audit",
    teaser:
      "A UX audit of a fishing-report platform — mapping where the product loses users across onboarding, navigation, and engagement, ranked by impact vs. effort.",
    thumb: "/case-studies/fishcaddy/homepage.png",
    accent: "from-[#4C1D95] to-[#7C3AED]",
  },
  {
    slug: "idx-broker-audit",
    n: "05",
    title: "IDX Broker",
    tag: "Competitive UX Audit",
    teaser:
      "A competitive UX benchmark of a real-estate platform against four rivals across five conversion funnels — 12 findings ranked by impact vs. effort.",
    thumb: "/case-studies/idx/idx-homepage.webp",
    accent: "from-[#4C1D95] to-[#A78BFA]",
  },
  {
    slug: "rehq-crm",
    n: "06",
    title: "REHQ",
    tag: "CRM Product UI",
    teaser:
      "UI for a commercial real-estate CRM — a dashboard, pipeline and deal records shaped around how brokers actually work, not a one-size-fits-all form.",
    thumb: "/case-studies/rehq/card.webp",
    accent: "from-[#4C1D95] to-[#7C3AED]",
  },
];

function Thumb({ item }) {
  if (item.thumb) {
    return (
      <div className="aspect-[4/3] overflow-hidden border-b border-[#0A0A0A] bg-[#EDE9FE]">
        <img
          src={item.thumb}
          alt={`${item.title} case study preview`}
          loading="lazy"
          className="w-full h-full object-cover object-top"
        />
      </div>
    );
  }
  return (
    <div
      className={`aspect-[4/3] border-b border-[#0A0A0A] bg-gradient-to-br ${item.accent} relative overflow-hidden flex items-end p-6`}
    >
      <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:18px_18px]" />
      <span className="relative font-display text-4xl md:text-5xl tracking-tighter text-white/90">
        {item.title}
      </span>
    </div>
  );
}

export default function SelectedWork() {
  return (
    <section
      id="work"
      className="border-b border-[#0A0A0A] scroll-mt-16"
      data-testid="selected-work-section"
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-24 md:py-36">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14 md:mb-20">
          <h2 className="font-display text-5xl md:text-7xl tracking-tighter">
            Selected Work<span className="text-[#4C1D95]">.</span>
          </h2>
          <p className="max-w-md text-[#595959] leading-relaxed text-lg">
            A few projects where I untangled complex products — from focused
            audits to end-to-end platform design.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {work.map((item) => (
            <Link
              key={item.slug}
              to={`/work/${item.slug}`}
              className="card-lift group block border border-[#0A0A0A] bg-white"
              data-testid={`work-card-${item.slug}`}
            >
              <Thumb item={item} />
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="label-mono text-[#595959]">
                    {item.n} / {item.tag}
                  </span>
                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#595959] transition-colors group-hover:text-[#4C1D95]"
                  />
                </div>
                <h3 className="font-display text-3xl tracking-tighter mb-3 transition-colors group-hover:text-[#4C1D95]">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base leading-relaxed text-[#595959]">
                  {item.teaser}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
